/**
 * SOVRANOVA — voiceTurn
 *
 * One spoken turn: open the microphone, hand the captured bytes to the
 * transcription endpoint exactly once, and let them go.
 *
 * THE PROMISE THIS FILE KEEPS:
 * the audio is never stored. It is not written to disk, not put in
 * localStorage or IndexedDB, not uploaded to any bucket, and not held in a
 * variable that outlives the turn. It exists as a Blob for the few hundred
 * milliseconds between "stop" and "transcribed", and then it is gone. The
 * server side matches this — VoiceService.transcribe streams the buffer
 * straight into the Whisper request without touching the filesystem.
 *
 * What IS kept is the text. That is the deliberate trade: a couple gets a
 * transcript they can both revisit and a companion that remembers, without a
 * recording of their marriage existing anywhere.
 *
 * WHY THIS IS A SERVICE AND NOT A HOOK: the same capture is needed by the
 * couples floor control, by Reflections, and eventually by the therapist
 * bridge. Three copies of a recorder is three places for the promise above to
 * quietly stop being true. One copy, one promise.
 */
import { api } from './api'

// Pick the best container this browser can actually produce. Chrome/Edge and
// Firefox give opus in webm/ogg; Safari only offers mp4. Whisper accepts all
// three, so the fallback chain never needs a transcode.
function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm'
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus'
  if (MediaRecorder.isTypeSupported('audio/webm'))             return 'audio/webm'
  if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus'))  return 'audio/ogg;codecs=opus'
  return 'audio/mp4'
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload  = () => resolve(String(reader.result).split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function micSupported() {
  return typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined'
}

/**
 * Start capturing. Resolves to a handle:
 *   await handle.stop()   -> transcript string ('' if nothing usable was said)
 *   handle.cancel()       -> throw the turn away, transcribe nothing
 *
 * Throws on permission denial or missing hardware so the caller can say
 * something useful rather than sitting there looking broken.
 */
export async function beginCapture() {
  if (!micSupported()) throw new Error('mic_unsupported')

  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

  let recorder
  try {
    recorder = new MediaRecorder(stream, { mimeType: pickMimeType() })
  } catch (err) {
    stream.getTracks().forEach(t => t.stop())   // never leave the mic light on
    throw err
  }

  let chunks = []
  let cancelled = false

  recorder.ondataavailable = e => { if (e.data && e.data.size > 0) chunks.push(e.data) }
  // If the OS reclaims the mic mid-turn, flush what we have rather than losing
  // everything the person just said.
  recorder.onerror = () => { try { recorder.stop() } catch { /* already stopping */ } }

  const ended = new Promise(resolve => { recorder.onstop = resolve })
  recorder.start(1000)   // emit a chunk every second — survives interruptions

  const releaseMic = () => { try { stream.getTracks().forEach(t => t.stop()) } catch {} }
  const halt = () => { try { if (recorder.state !== 'inactive') recorder.stop() } catch {} }

  return {
    get mimeType() { return recorder.mimeType },

    cancel() {
      cancelled = true
      halt()
      releaseMic()
      chunks = []
    },

    async stop() {
      halt()
      await ended
      releaseMic()

      if (cancelled) { chunks = []; return '' }

      const blob = new Blob(chunks, { type: recorder.mimeType })
      chunks = []                       // drop the raw chunks immediately
      if (!blob.size) return ''         // nothing captured — stay quiet, don't error

      const audio = await blobToBase64(blob)
      const mimeType = recorder.mimeType

      const send = () => api.post('/voice/transcribe', { audio, mimeType })
      let text = ''
      try {
        ;({ text } = await send())
      } catch (first) {
        // One retry: a phone that just woke may still be re-establishing the
        // network. A second failure is a real failure and propagates.
        await new Promise(r => setTimeout(r, 800))
        ;({ text } = await send())
      }
      return String(text || '').trim()
    },
  }
}
